// components/admin/TicketManagement.js
import { format } from 'date-fns';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  FilterIcon, MailIcon,
  MessageSquareIcon,
  Paperclip,
  RefreshCwIcon, SearchIcon,
  ShoppingBagIcon,
  TagIcon,
  UserIcon,
  XCircleIcon
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNotification } from '../../context/NotificationContext';
import axios from '../../utils/axiosHandler';
import AttachmentViewer from '../AttachmentViewer';

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [viewingAttachment, setViewingAttachment] = useState(null);
  const { updateTicketCount } = useNotification();

  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: '',
    page: 1
  });
  const [stats, setStats] = useState({
    counts: { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 },
    categories: []
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [filters]);

  useEffect(() => {
    // Only update when we have valid data and are not loading
    if (!loading && stats?.counts) {
      const openCount = stats.counts.open || 0;
      // Call this only once per stats update
      updateTicketCount(openCount);
    }
  }, [stats?.counts?.total]);
  const fetchTickets = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      const params = {
        status: filters.status,
        category: filters.category,
        search: filters.search,
        page: filters.page,
        limit: 10
      };

      const response = await axios.get('/api/tickets/admin/list', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });

      setTickets(response.data.tickets);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };


  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/tickets/admin/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch ticket statistics');
      }

      const data = await response.data;
      setStats(data);
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch ticket details');
      }

      const data = await response.data;
      setSelectedTicket(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const response = await axios.put(`/api/tickets/${ticketId}/status`, {
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to update ticket status');
      }

      // Update the selected ticket if it's the one being modified
      if (selectedTicket && selectedTicket._id === ticketId) {
        const updatedTicket = { ...selectedTicket, status: newStatus };
        setSelectedTicket(updatedTicket);
      }

      // Refresh the tickets list and stats
      fetchTickets();
      fetchStats();

      setMessage({ type: 'success', text: 'Ticket status updated successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();

    if (!replyText.trim()) {
      setMessage({ type: 'error', text: 'Reply message cannot be empty' });
      return;
    }

    try {
      const response = await axios.post(`/api/tickets/${selectedTicket._id}/responses`, {
        message: replyText
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      });

      if (response.status !== 200) {
        throw new Error('Failed to send reply');
      }

      const data = await response.data;
      setSelectedTicket(data.ticket);
      setReplyText('');

      setMessage({ type: 'success', text: 'Reply sent successfully' });
      setTimeout(() => setMessage(null), 3000);

      // Refresh the tickets list
      fetchTickets();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const renderPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircleIcon className="text-red-500" />;
      case 'high':
        return <AlertCircleIcon className="text-orange-500" />;
      case 'medium':
        return <AlertCircleIcon className="text-yellow-500" />;
      case 'low':
        return <AlertCircleIcon className="text-green-500" />;
      default:
        return <AlertCircleIcon className="text-gray-500" />;
    }
  };

  const renderStatusBadge = (status) => {
    let bgColor, textColor, statusText;

    switch (status) {
      case 'open':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        statusText = 'Open';
        break;
      case 'in-progress':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        statusText = 'In Progress';
        break;
      case 'resolved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        statusText = 'Resolved';
        break;
      case 'closed':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        statusText = 'Closed';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        statusText = status;
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {statusText}
      </span>
    );
  };


  const renderCategoryIcon = (category) => {
    switch (category) {
      case 'order':
        return <ShoppingBagIcon className="h-4 w-4" />;
      case 'product':
        return <TagIcon className="h-4 w-4" />;
      case 'shipping':
        return <RefreshCwIcon className="h-4 w-4" />;
      case 'payment':
        return <CreditCardIcon className="h-4 w-4" />;
      case 'account':
        return <UserIcon className="h-4 w-4" />;
      default:
        return <MessageSquareIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <h2 className="text-2xl font-semibold">Support/ Ticket Management</h2>
      <div className="container mx-auto p-4 bg-gray-50 order border-gray-200 rounded-2xl">


        {/* Notification Message */}
        {message && (
          <div className={`p-4 mb-4 rounded-md ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message.text}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Tickets</h3>
            <p className="text-2xl font-bold">{stats.counts.total}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-blue-700">Open</h3>
            <p className="text-2xl font-bold text-blue-700">{stats.counts.open}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-yellow-700">In Progress</h3>
            <p className="text-2xl font-bold text-yellow-700">{stats.counts.inProgress}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-green-700">Resolved</h3>
            <p className="text-2xl font-bold text-green-700">{stats.counts.resolved}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-700">Closed</h3>
            <p className="text-2xl font-bold text-gray-700">{stats.counts.closed}</p>
          </div>
        </div>

        {/* Filter and Search */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col lg:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
            <div className="flex items-center space-x-2">
              <FilterIcon className="h-5 w-5 text-gray-400" />
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <TagIcon className="h-5 w-5 text-gray-400" />
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
                className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              >
                <option value="all">All Categories</option>
                <option value="order">Order</option>
                <option value="product">Product</option>
                <option value="shipping">Shipping</option>
                <option value="payment">Payment</option>
                <option value="account">Account</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex-1 flex items-center relative">
              <SearchIcon className="h-5 w-5 text-gray-400 absolute left-3" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="form-input pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>

            <button
              onClick={() => fetchTickets()}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <RefreshCwIcon className="h-4 w-4 mr-2" /> Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 lg:space-x-6">
          {/* Tickets List */}
          <div className="w-full lg:w-1/3">
            <h2 className="text-lg font-semibold mb-4">Tickets</h2>
            {loading ? (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
                <p className="mt-4">Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <MessageSquareIcon className="h-12 w-12 text-gray-400 mx-auto" />
                <p className="mt-4 text-gray-500">No tickets found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    onClick={() => fetchTicketDetails(ticket._id)}
                    className={`p-4 bg-white rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow ${selectedTicket && selectedTicket._id === ticket._id ? 'ring-2 ring-indigo-500' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium truncate" title={ticket.subject}>
                        {ticket.subject}
                      </h3>
                      {renderStatusBadge(ticket.status)}
                    </div>

                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <UserIcon className="h-4 w-4 mr-1" />
                      <span className="truncate" title={`${ticket.name} (${ticket.email})`}>
                        {ticket.name}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-500">
                        {renderCategoryIcon(ticket.category)}
                        <span className="ml-1 capitalize">{ticket.category}</span>
                      </div>
                      <div className="text-gray-500">
                        <ClockIcon className="h-4 w-4 inline mr-1" />
                        {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
                    disabled={filters.page === 1}
                    className="px-3 py-1 rounded-md mr-2 bg-gray-100 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">Page {filters.page}</span>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={tickets.length < 10}
                    className="px-3 py-1 rounded-md ml-2 bg-gray-100 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Selected Ticket Details */}
          <div className="w-full lg:w-2/3">
            {selectedTicket ? (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Ticket Header */}
                <div className="p-6 border-b">
                  <div className="flex justify-between">
                    <h2 className="text-xl font-semibold">{selectedTicket.subject}</h2>
                    <div>
                      {renderStatusBadge(selectedTicket.status)}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">From:</span>
                      <div className="flex items-center mt-1">
                        <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{selectedTicket.name}</span>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Created:</span>
                      <div className="flex items-center mt-1">
                        <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{format(new Date(selectedTicket.createdAt), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Email:</span>
                      <div className="flex items-center mt-1">
                        <MailIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a href={`mailto:${selectedTicket.email}`} className="text-blue-600 hover:underline">
                          {selectedTicket.email}
                        </a>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500">Category:</span>
                      <div className="flex items-center mt-1">
                        {renderCategoryIcon(selectedTicket.category)}
                        <span className="ml-2 capitalize">{selectedTicket.category}</span>
                      </div>
                    </div>

                    {selectedTicket.orderId && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Order ID:</span>
                        <div className="flex items-center mt-1">
                          <ShoppingBagIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <a href={`/admin/orders/${selectedTicket.orderId}`} className="text-blue-600 hover:underline">
                            {selectedTicket.orderId}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ticket Messages */}
                <div className="p-6 max-h-96 overflow-y-auto bg-gray-50">
                  {/* Original Message */}
                  <div className="bg-white p-4 rounded-lg shadow mb-4">
                    <div className="flex justify-between text-sm text-gray-500 mb-2">
                      <span>{selectedTicket.name}</span>
                      <span>{format(new Date(selectedTicket.createdAt), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{selectedTicket.message}</div>


                    {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Attachments:</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedTicket.attachments.map((url, index) => {
                            // Get file extension to determine icon/thumbnail
                            const extension = url.split('.').pop().toLowerCase();
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);

                            return (
                              <button
                                key={index}
                                onClick={() => setViewingAttachment(url)}
                                className="flex items-center bg-gray-100 px-2 py-1 rounded text-sm hover:bg-gray-200 transition-colors"
                              >
                                {isImage ? (
                                  <div className="w-5 h-5 mr-1 relative overflow-hidden bg-gray-200 rounded">
                                    <img
                                      src={url}
                                      alt=""
                                      className="absolute inset-0 w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                    <Paperclip className="h-4 w-4 absolute inset-0 m-auto hidden" />
                                  </div>
                                ) : (
                                  <Paperclip className="h-4 w-4 mr-1" />
                                )}
                                Attachment {index + 1}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Responses */}
                  {selectedTicket.responses && selectedTicket.responses.map((response, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg shadow mb-4 ${response.createdBy === 'admin'
                        ? 'bg-indigo-50 ml-8'
                        : 'bg-white mr-8'
                        }`}
                    >
                      <div className="flex justify-between text-sm text-gray-500 mb-2">
                        <span>{response.createdBy === 'admin' ? 'Support Agent' : selectedTicket.name}</span>
                        <span>{format(new Date(response.createdAt), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                      <div className="whitespace-pre-wrap">{response.message}</div>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                <div className="p-6 border-t">
                  <form onSubmit={handleReplySubmit}>
                    <div className="mb-4">
                      <label htmlFor="reply" className="block text-sm font-medium text-gray-700 mb-2">
                        Reply
                      </label>
                      <textarea
                        id="reply"
                        rows="4"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        placeholder="Type your reply here..."
                      ></textarea>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        {selectedTicket.status !== 'closed' && (
                          <>
                            {selectedTicket.status !== 'resolved' && (
                              <button
                                type="button"
                                onClick={() => handleStatusChange(selectedTicket._id, 'resolved')}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-1" /> Mark Resolved
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleStatusChange(selectedTicket._id, 'closed')}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              <XCircleIcon className="h-4 w-4 mr-1" /> Close Ticket
                            </button>
                          </>
                        )}

                        {selectedTicket.status === 'closed' && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(selectedTicket._id, 'open')}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <RefreshCwIcon className="h-4 w-4 mr-1" /> Reopen Ticket
                          </button>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Send Reply
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white p-8 rounded-lg shadow text-center">
                <MessageSquareIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No ticket selected</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Select a ticket from the list to view its details and respond
                </p>
              </div>
            )}
          </div>
        </div>
        {viewingAttachment && (
          <AttachmentViewer
            url={viewingAttachment}
            onClose={() => setViewingAttachment(null)}
          />
        )}
      </div>
    </div>
  );
};

export default TicketManagement;